// MapPoiInfoInput.js
import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Box } from '@mui/system';
import { Button, Chip } from '@mui/material';
import ValueMisc from '../../value/ValueMisc';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppNumber, AppObject, AppStyle, AppWord } from '../../value/AppConst';

const PoiInfInputContainer = styled(Box)({
	flexGrow:1, display:'flex', flexDirection:'column'
});
const HeaderBox = styled(Box)({
    display:'flex', alignItems:'center', justifyContent:AppWord.END, padding: AppNumber.SmallBoxPadding,
});
const BodyBox = styled(Box)({ flexGrow:1, overflow:'auto'});
const RowBox = styled(Box)({padding:"5px 2px 5px 10px"});

const LabelBox = styled('div')({fontSize:'0.85rem', color:AppStyle.PrimaryRGB, padding:"10px 2px 5px 5px"});
const GuideBox = styled(Box)({
    fontSize: '0.9rem', color:'#345', padding:"5px 2px 5px 10px"
});

const defaultInputData = {radius:0,xDist:1, yDist:1, lat:0, lon:0};

const EmptyDMS = '__도 __분 __초';

// TODO : 지점그룹 사용하는 경우 그룹을 선택하는 컨트롤을 추가해야 함.

/**
 * DC 또는 Store의 Polygon정보 생성 및 수정용 컴퍼넌트.
 * DC 또는 Store 자체의 생성은 여기서 하지 않음. (그건 지도 아닌 화면에서)
 * Dialog 사용하지 않음. 좌측에 지점목록과 배타적으로 위치를 차지함.
 */
export default function MapPoiInfoInput({
    open,
    mapShell,
    item,
    onRequestSetDcZone,
    onRequestSetStoreZone,
    onClose, // Cancel editing.
}) {
    const [changed, setChanged] = useState(false); // 입력으로 인한 변경이 있는가?
    const [inputData, setInputData] = useState(defaultInputData); // 입력한 데이터.
    const [hasError, setHasError] = useState(false); // 하나라도 오류가 있는가? 입력되지 않은 것이 있는가?
    const [posText, setPosText] = useState(null);
    const [polygon, setPolygon] = useState([]); // Polygon working (drawing now)
    const [polygonArea, setPolygonArea] = useState(null);
    const [posChanged, setPosChanged] = useState({}); // inputData와 새로 움직이는 마커 포지현의 동시 처리가 쉽지 않아 별도로....
    const openConfirmDialog = useConfirmDialog();
    const posChangedWhen = useRef();

    // /NTOPMAP_DEV/hmimg/poi/store_red_18x18.png

    useEffect(()=>{
        if(open && mapShell) {
            const onMoveMarker = (pos) => {
                const data = {...pos};
                data.lat = pos.lat;
                data.lon = pos.lon;
                const {x, y} = mapShell.ll2katek(pos);
                data.xCoord = Math.round(x);
                data.yCoord = Math.round(y);
                data.when = new Date().getTime();
                setPosChanged(data); // 마우스로 아이콘을 움직이면 state변경.
            };
            // 신규 생성은 여기서는 하지 않는다.
            const copy = {...item};
            // 위치가 없는 경우 중심위치를 가져와서 설정한다.
            if(item.xCoord<10 || item.yCoord<10) {
                const pos = mapShell.getCenter();
                const xy = mapShell.ll2katek(pos);
                copy.lat = pos.lat;
                copy.lon = pos.lon;
                copy.xCoord = Math.round(xy.x);
                copy.yCoord = Math.round(xy.y);
            }
            setInputData(copy);
            // dragend 이벤트를 받아서 위치를 변경할 수 있도록 여기서 설정. 닫는 것은 부모 컴포넌트에서.
            mapShell.setOpenPoiEditor(true, copy, null, null, onMoveMarker); // dragend Event.
            // 예전에는 지점위치 변경을 위해 컨텍스트 메뉴 사용했으나, 이번에는 직접 마커를 움직이게 한다.
            // 기존 마커 위에 새 마커를 띄우고 드랙하면 된대.
        }
    }, [mapShell, open, item]); // inputData 추가하지 말 것. (무한루프)

    // 마우스로 아이콘을 움직이면 state변경된 것을 받아서 inputData에 반영한다.
    useEffect(()=>{
        if(posChanged.when && posChanged.when !== posChangedWhen.current) {
            const data = {...inputData};
            data.lat = posChanged.lat;
            data.lon = posChanged.lon;
            data.xCoord = posChanged.xCoord;
            data.yCoord = posChanged.yCoord;
            posChangedWhen.current = posChanged.when;
            setInputData(data);
            setChanged(true);
        }
    }, [posChanged, inputData, mapShell]);

    // 현재 설정된 위치를 볼 수 있도록.
    useEffect(()=>{
        const lat = inputData.lat > 0 ? ValueMisc.degreeToText(inputData.lat) : EmptyDMS;
        const lon = inputData.lon > 0 ? ValueMisc.degreeToText(inputData.lon) : EmptyDMS;
        setPosText({lat:lat, lon:lon});
    }, [inputData]);

	const resetData = (data) => {
		setInputData(data ? data : defaultInputData);
		setChanged(false);
		setHasError(false);
        setPolygon([]);
	};

    // 지도 앱의 복잡성 때문에 이 곳에서 처리 (보통은 상위의 컨트롤러에서 처리)
    // ##### GraphQL Mutation.  ###

	// 저장 호출은 컨트롤러로 보내고, 그 결과를 responseSaving 받아서 보여준다.
    const onClickSubmit = () => {
        const param = {
            useZone: inputData.useZone,
            zoneShape: inputData.zoneShape,
            lat: inputData.lat,
            lon: inputData.lon,
            xCoord: inputData.xCoord,
            yCoord: inputData.yCoord,
        }
        if(item.poiGroup === 'DC') {
            param.dcCd = item.dcCd;
            onRequestSetDcZone(param);
        }
        else {
            param.storeId = item.storeId;
            onRequestSetStoreZone(param);
        }

        /* const data = ValueUtil.getDataToSubmit(inputData, InputMap, isEdit); // {...inputData};
        const {x, y} = mapShell.ll2katek({lat:data.lat, lon:data.lon});
        data.xDist = x;
        data.yDist = y;
        editPoiInfo({variables: {poiInfo:data}}); */
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) {
            resetData();
            onClose();
        }
    }

    const onClickCancel = () => {
        if(changed) {
            askCancel();
        }
        else {
            resetData();
            onClose();
        }
    };

    // 새로운 Polygon을 그릴 준비.
    const prepareNewPolygon = () => {
        let plat = item.lat;
        let plon = item.lon;

        const dLon = 0.0017; // * 3; // 약 111미터 (0.001 / sin(30deg)=0.588)
        const dLat = 0.001; // * 3; // 약 111미터

        const left = plon - dLon;
        const right = plon + dLon;
        const top = plat + dLat;
        const bottom = plat - dLat;
        const positions = [
            {lat:top, lon:left},
            {lat:top, lon:right},
            {lat:bottom, lon:right},
            {lat:bottom, lon:left}
        ];
        setPolygon(positions);
        mapShell.setEditingPoiPolygon(positions);
    };

    // 기존 Polygon을 편집할 준비.
    const prepareEditPolygon = () => {
        mapShell.setEditingPoiPolygonShape(inputData.zoneShape);
        setPolygon(mapShell.getPolygonWork().getList());
    };

    const onClickStartDrawingPolygon = () => {
        if(inputData.useZone && inputData.zoneShape) prepareEditPolygon();
        else prepareNewPolygon();
    };

    const clearWorkingPolygon = () => {
        mapShell.setEditingPoiPolygon([]);
        setPolygon([]);
    };

    const onClickCancelDrawingPolygon = () => {
        //mapShell.clearPoiPolygon();
        // 이전에 데이터를 가지고 있는 (편집중인) 지점이라면 기존 폴리곤을 넣어준다.
        clearWorkingPolygon();
        mapShell.drawCurrentPolyginWhileNotAdujusting(inputData);
    };

    const saveInputData = (newData) => {
        setInputData(newData);
        setChanged(true);
    };

    const onClickRemovePolygon = () => {
        const newData = {...inputData};
        newData.useZone = false;
        newData.zoneShape = null;
        saveInputData(newData);
        // mapShell.drawCurrentPolyginWhileNotAdujusting(newData);
        //mapShell.clearPoiPolygon();
        mapShell.setEditingPoiPolygon([]);
    };

    // polygon 확정.
    const onClickSavePolygon = () => {
        const area = mapShell.getEditingPolygonArea();
        setPolygonArea(Math.round(area));

        const polygonWork = mapShell.getPolygonWork();
        const data = {...inputData};
        data.zoneShape = polygonWork.makeZoneShape();
        data.useZone = true;
        
        saveInputData(data);
        clearWorkingPolygon();
        mapShell.drawCurrentPolyginWhileNotAdujusting(data);
    };


    // redner -------------------

    const renderPolygonStatus = () => {
        if(polygon.length>2) {
            return(
                <>
                <GuideBox>
                    <ul>
                        <li>꼭지점 위치 변경:
                            검은색 원을 마우스로 끌어서 꼭지점 위치를 바꿀 수 있습니다.
                        </li>
                        <li>꼭지점 추가:
                            꼭지점 사이의 빨간색 원을 마우스로 끌어 놓으면 그 위치에 새로운 꼭지점이 추가 됩니다.
                        </li>
                        <li>꼭지점 삭제:
                            검은색 원을 마우스로 우클릭하면 그 꼭지점이 삭제됩니다.
                        </li>
                        <li>완료하기:
                            구역 확정 버튼을 눌러 변경을 완료하고, 그 후 저장해야 온전히 반영됩니다.
                        </li>
                    </ul>
                </GuideBox>
                <RowBox>
                    <Button
                        size={AppWord.SMALL}
                        onClick={onClickSavePolygon}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                    >구역 확정</Button>
                    &nbsp;
                    <Button size={AppWord.SMALL}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.warning}
                        onClick={onClickCancelDrawingPolygon}
                    >구역변경 취소</Button>
                </RowBox>
                </>
            );
        }
        else {
            return(
                <>
                <GuideBox>
                    {
                        inputData.useZone
                        ? '설정 삭제 버튼을 누르면 확인하지 않고 삭제합니다'
                        : '출발, 도착 감지가 필요하지 않은 경우는 생성하지 않아도 됩니다.'
                    }
                </GuideBox>
                <RowBox>
                    <Button size={AppWord.SMALL}
                        onClick={onClickStartDrawingPolygon}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                    >설정 {inputData.useZone ? '변경' : '시작'}</Button>
                    &nbsp;
                    {
                        inputData.useZone ?
                        <Button size={AppWord.SMALL}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.warning}
                            onClick={onClickRemovePolygon}
                        >설정 삭제</Button>
                        : null
                    }
                </RowBox>
                </>
            );
        }
    };


    return (
        <PoiInfInputContainer>
            <HeaderBox>
                <Button variant={AppStyle.mui.contained}
                    disabled={hasError || polygon.length > 0 || !changed}
                    onClick={onClickSubmit}
                >
                    저장
                </Button>
                &nbsp;
                <Button variant={AppStyle.mui.contained} color={AppStyle.mui.warning}
                    onClick={onClickCancel}
                >
                    취소
                </Button>
            </HeaderBox>
            <BodyBox>
                <LabelBox>
                    좌표 (아이콘을 마우스로 움직일 수 있습니다)
                </LabelBox>
                {
                    item.xCoord < 10 || item.yCoord < 10
                    ?
                    <LabelBox>
                        현재 좌표가 설정되어 있지 않아 현위치로 설정하였습니다.
                        위치를 설정할 곳으로 풍선 마커를 이동하세요.
                    </LabelBox>
                    : null
                }
                <RowBox>
                    <Chip label={`위도: 북위 ${posText ? posText.lat : '...'}`}
                        variant={AppStyle.mui.outlined} />
                </RowBox>
                <RowBox>
                    <Chip label={`경도: 동경 ${posText ? posText.lon : '...'}`}
                        variant={AppStyle.mui.outlined} /></RowBox>
                <LabelBox>
                    출발, 도착 감지구역 ({inputData.useZone ? '설정됨.' + (polygonArea ? ` (${polygonArea} 평방미터)` : '') : '현재 설정 없음'})
                </LabelBox>
                {renderPolygonStatus()}
            </BodyBox>
        </PoiInfInputContainer>
    )
}
